import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/hooks/useLanguage';
import { useResponsive } from '@/hooks/use-responsive';
import { AppTourEngine } from './AppTourEngine';
import { APP_TOUR_EXCLUDED_ROUTE_PREFIXES } from './constants';
import { resetTourState, setTourStatus, shouldAutoShowTour } from './storage';
import type { StartTourOptions } from './types';
import { WelcomeTourModal } from './WelcomeTourModal';
import './AppTour.css';

interface AppTourContextValue {
  startTour: (options?: StartTourOptions) => void;
  skipTour: () => void;
  isTourActive: boolean;
  isWelcomeVisible: boolean;
  shouldDeferSecondaryPrompts: boolean;
}

export const AppTourContext = createContext<AppTourContextValue | null>(null);

function isExcludedRoute(pathname: string): boolean {
  return APP_TOUR_EXCLUDED_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function AppTourProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, currentUserProfile } = useAuth();
  const { t } = useLanguage();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();

  const [isWelcomeVisible, setIsWelcomeVisible] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false);
  const engineRef = useRef<AppTourEngine | null>(null);
  const autoShowAttemptedRef = useRef(false);

  const hasCompletedProfileOnboarding =
    currentUserProfile?.hasCompletedOnboarding === true;

  const canRunTour =
    isAuthenticated &&
    hasCompletedProfileOnboarding &&
    !isExcludedRoute(location.pathname);

  const destroyEngine = useCallback(() => {
    engineRef.current?.destroy();
    engineRef.current = null;
    setIsTourActive(false);
  }, []);

  const skipTour = useCallback(() => {
    destroyEngine();
    setIsWelcomeVisible(false);
    setTourStatus('skipped');
  }, [destroyEngine]);

  const beginTour = useCallback(
    async (fromIndex = 0) => {
      destroyEngine();
      setIsWelcomeVisible(false);
      setIsTourActive(true);

      const engine = new AppTourEngine({
        t,
        isMobile,
        getPathname: () => window.location.pathname,
        navigate: (path) => navigate(path),
        onComplete: () => {
          setTourStatus('completed');
          setIsTourActive(false);
          engineRef.current = null;
        },
        onSkip: () => {
          setTourStatus('skipped');
          setIsTourActive(false);
          engineRef.current = null;
        },
        onStepChange: () => {},
      });

      engineRef.current = engine;
      await engine.start(fromIndex);
    },
    [destroyEngine, isMobile, navigate, t],
  );

  const startTour = useCallback(
    (options?: StartTourOptions) => {
      if (options?.force) {
        resetTourState();
      }

      if (options?.skipWelcome) {
        void beginTour(0);
        return;
      }

      setIsWelcomeVisible(true);
    },
    [beginTour],
  );

  const handleWelcomeStart = useCallback(() => {
    void beginTour(0);
  }, [beginTour]);

  const handleWelcomeSkip = useCallback(() => {
    skipTour();
  }, [skipTour]);

  useEffect(() => {
    if (!canRunTour) {
      autoShowAttemptedRef.current = false;
      return;
    }

    if (autoShowAttemptedRef.current) {
      return;
    }

    if (!shouldAutoShowTour()) {
      return;
    }

    autoShowAttemptedRef.current = true;
    setIsWelcomeVisible(true);
  }, [canRunTour]);

  useEffect(() => {
    if (!isTourActive || !engineRef.current) {
      return;
    }

    engineRef.current.handleRouteChange(location.pathname);
    window.dispatchEvent(new Event('biavent-tour-route-change'));
  }, [isTourActive, location.pathname]);

  useEffect(() => {
    return () => {
      destroyEngine();
    };
  }, [destroyEngine]);

  const shouldDeferSecondaryPrompts = isWelcomeVisible || isTourActive;

  const value = useMemo<AppTourContextValue>(
    () => ({
      startTour,
      skipTour,
      isTourActive,
      isWelcomeVisible,
      shouldDeferSecondaryPrompts,
    }),
    [isTourActive, isWelcomeVisible, shouldDeferSecondaryPrompts, skipTour, startTour],
  );

  return (
    <AppTourContext.Provider value={value}>
      {children}
      {isWelcomeVisible && (
        <WelcomeTourModal onStart={handleWelcomeStart} onSkip={handleWelcomeSkip} />
      )}
    </AppTourContext.Provider>
  );
}
